import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import { getManualAuthConfig } from '../utils/configLoader';

const Login = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [initializing, setInitializing] = useState(true);
    const [loginAttempted, setLoginAttempted] = useState(false);
    
    // Auto-login if there are accounts but none active
    useEffect(() => {
        const checkAccounts = async () => {
            try {
                // Ensure MSAL is initialized
                if (!instance.initialized) {
                    console.log("Login: Waiting for MSAL to initialize...");
                    // Wait a moment for initialization
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                
                const allAccounts = instance.getAllAccounts();
                console.log("Login: Accounts found:", allAccounts.length);
                
                // If we have accounts but no active account, set the first one active
                if (allAccounts.length > 0 && !instance.getActiveAccount()) {
                    console.log("Login: Setting active account:", allAccounts[0].name);
                    instance.setActiveAccount(allAccounts[0]);
                    // Force a reload to ensure the authenticated state is properly recognized
                    window.location.reload();
                }
                
                setInitializing(false);
            } catch (error) {
                console.error("Login: Error checking accounts:", error);
                setInitializing(false);
            }
        };
        
        checkAccounts();
    }, [instance, accounts]);
    
    const handleLogin = async () => {
        setLoginAttempted(true);
        
        // Log any existing accounts for debugging
        const allAccounts = instance.getAllAccounts();
        if (allAccounts.length > 0) {
            console.log("Login: Existing accounts found:", allAccounts.map(a => a.username || a.name));
            
            // If we have accounts, just set one active instead of logging in again
            if (!instance.getActiveAccount()) {
                console.log("Login: Setting active account from existing accounts");
                instance.setActiveAccount(allAccounts[0]);
                // Force a reload to refresh the auth state
                window.location.reload();
                return; // Exit early - no need to log in again
            }
        } else {
            console.log("Login: No existing accounts found, proceeding with login");
        }
        
        // Get configuration values from runtime config
        try {
            var authConfig = getManualAuthConfig();
        } catch (error) {
            console.error("Configuration not loaded for login:", error);
            return;
        }

        // Open Azure AD login page directly
        // Use id_token for better backend compatibility
        const tenantId = authConfig.tenantId;
        const clientId = authConfig.clientId;
        const redirectUri = encodeURIComponent(authConfig.redirectUri);
        const responseType = "id_token+token"; // Get both ID token and access token
        const scope = encodeURIComponent("openid profile email User.Read");
        const nonce = Math.random().toString(36).substring(2, 15);
        const state = Math.random().toString(36).substring(2, 15);
        
        // Store state and nonce in session storage to verify later
        sessionStorage.setItem("loginState", state);
        sessionStorage.setItem("loginNonce", nonce);
        
        // Construct the authorization URL for implicit flow with ID token
        const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
            `client_id=${clientId}` +
            `&response_type=${responseType}` +
            `&redirect_uri=${redirectUri}` +
            `&scope=${scope}` +
            `&state=${state}` +
            `&nonce=${nonce}` +
            `&response_mode=fragment` +
            `&prompt=select_account`;
        
        console.log("Login: Redirecting to authorization endpoint with implicit flow");
        
        // Redirect to the authorization endpoint
        window.location.href = authUrl;
    };
    
    // Show appropriate loading or login state
    if (initializing) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Checking authentication status...</p>
            </div>
        );
    }
    
    // Show loading during the auth process
    if (inProgress === "login" || loginAttempted) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Signing in with Microsoft...</p>
                <p className="text-muted small">
                    If the login process seems stuck, please refresh the page and try again.
                </p>
            </div>
        );
    }
    
    return (
        <div className="text-center my-5 p-5 bg-light rounded">
            <h2 className="mb-4">Please sign in</h2>
            <p className="mb-4">
                You need to sign in with your Microsoft account to use this application.
            </p>
            <button 
                className="btn btn-primary btn-lg"
                onClick={handleLogin}
                disabled={inProgress === "login"}
            >
                Sign in with Microsoft
            </button>
        </div>
    );
};

export default Login;